import {
  getResolver,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@folio/components/forms';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  brokerageFirmControllerGenerateReport,
  brokerageFirmControllerGetReportConfigQueryOptions,
  configControllerGetPublicTenantConfigQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
  type GenerateReportInputBody,
  type GenerateReportInputBodyTypeEnumKey,
} from 'korint-sdk';
import { type Field } from 'korint-sdk/react';
import { useTranslation } from 'react-i18next';
import { Wand2Icon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { FormBuilder } from '../../forms/form-builder/FormBuilder';
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton, toast } from '../../ui';
import { get } from 'lodash';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

function GenerateBrokerageFirmReportsForm({
  reportType,
  brokerageFirmId,
}: {
  reportType: GenerateReportInputBodyTypeEnumKey;
  brokerageFirmId: string;
}) {
  const { t } = useTranslation();

  const { data: reportConfig, isLoading } = useQuery({
    ...brokerageFirmControllerGetReportConfigQueryOptions(brokerageFirmId, reportType),
  });

  const { availableProductIds = [], params = [] } = reportConfig ?? {};
  const isOnlyOneProductAvailable = availableProductIds?.length === 1;

  const translatedProducts = availableProductIds.map(productId => ({
    value: productId,
    label: t(`products.${productId}.label`),
  }));

  const fields: Field[] = [
    {
      name: 'reportProductsSelect',
      ...(isOnlyOneProductAvailable
        ? {
            type: 'select',
            disabled: true,
            required: false,
          }
        : {
            type: 'multiSelect',
            DEPRECATED_placeholder: t('reports.productsPlaceholder'),
            required: true,
          }),
      options: translatedProducts,
    },
    ...params.map(({ key, type, validationRules, required, defaultValue }) => {
      const isRequired = Boolean(required) && defaultValue === undefined;

      if (type === 'ENUM') {
        return {
          name: key,
          type: 'select' as const,
          required: isRequired,
          options: (validationRules?.find(rule => rule.option === 'enum')?.value || []) as string[],
        };
      }

      if (type === 'DATETIME') {
        return {
          name: key,
          type: 'datetime' as const,
          required: isRequired,
        };
      }

      return {
        name: key,
        type: 'text' as const,
        required: isRequired,
      };
    }),
  ];

  const form = useForm<FormValues>({
    resolver: getResolver(fields),
  });

  const { isPending, mutateAsync: generateReport } = useMutation({
    mutationFn: async ({
      type,
      productIds,
      params,
    }: {
      type: GenerateReportInputBodyTypeEnumKey;
      productIds: string[];
      params: { key: string; value: string }[];
    }) =>
      await brokerageFirmControllerGenerateReport(brokerageFirmId, {
        productIds,
        type,
        params,
      }),
    onSuccess: () => {
      toast({
        title: t('reports.toast.successTitle'),
        description: t('reports.toast.successMessage'),
        color: 'green',
      });
    },
  });

  useEffect(() => {
    if (isOnlyOneProductAvailable) {
      form.setValue('reportProductsSelect', availableProductIds[0]);
    }
  }, [isOnlyOneProductAvailable, availableProductIds]);

  const values = form.watch();

  useEffect(() => {
    for (const param of params) {
      if (param.defaultValue !== undefined) {
        if (values[param.key as keyof FormValues] === undefined) {
          form.setValue(
            param.key as keyof FormValues,
            param.type === 'DATETIME'
              ? dayjs(param.defaultValue).tz('Europe/Paris').format('YYYY-MM-DD HH:mm')
              : param.defaultValue,
          );
        }
      }
    }
  }, [values, params]);

  if (isLoading) {
    return <Skeleton className="h-60 w-full" />;
  }

  if (availableProductIds.length === 0) {
    return <p>{t('reports.noProductAvailable')}</p>;
  }

  return (
    <FormBuilder fields={fields} form={form}>
      <Button
        type="submit"
        icon={<Wand2Icon size="1rem" className="mr-2" />}
        loading={isPending}
        onClick={form.handleSubmit(async values => {
          // The single-product select is disabled, so its value can be absent at submit
          const selectedProductIds = [values.reportProductsSelect]
            .flat()
            .filter(Boolean) as string[];

          await generateReport({
            type: reportType as GenerateReportInputBodyTypeEnumKey,
            productIds: selectedProductIds.length ? selectedProductIds : availableProductIds,
            params: params
              .map(param => ({
                key: param.key,
                value:
                  param.type === 'DATETIME' && get(values, param.key)
                    ? dayjs(get(values, param.key)).tz('Europe/Paris').utc().toISOString()
                    : get(values, param.key),
              }))
              .filter(({ value }) => Boolean(value)) as { key: string; value: string }[],
          });
        })}
      >
        {t('reports.generateButton')}
      </Button>
    </FormBuilder>
  );
}

type FormValues = {
  reportProductsSelect: string[] | string;
  type: GenerateReportInputBodyTypeEnumKey;
} & Record<string, string | undefined>;

export type ReportsCardProps = {
  title: ReactNode;
  onTypeChange?: (type: GenerateReportInputBody['type']) => void;
};

export function GenerateBrokerageFirmReportsCard({ title, onTypeChange }: ReportsCardProps) {
  const { t } = useTranslation();

  const { data: user } = useSuspenseQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { data: availableReports = [] } = useQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
    select: data =>
      data?.reports
        // An automation-only report is still listed and downloadable, just never triggered here.
        ?.filter(
          ({ allowedBusinessRoles, automationOnly }) =>
            !automationOnly && allowedBusinessRoles?.includes(user?.brokerRole!),
        )
        ?.map(({ type }) => type),
  });

  const [reportType, setReportType] = useState<string | undefined>();

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Select
            value={reportType}
            onValueChange={value => {
              setReportType(value);
              onTypeChange?.(value as GenerateReportInputBody['type']);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('reports.selectPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {availableReports.map(type => (
                <SelectItem key={type} value={type}>
                  {t(`reports.types.${type}`, { defaultValue: type })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {Boolean(reportType) && Boolean(user.brokerageFirmId) && (
            <GenerateBrokerageFirmReportsForm
              reportType={reportType as GenerateReportInputBodyTypeEnumKey}
              brokerageFirmId={user.brokerageFirmId!}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
}
